<template>
  <!-- Öffentlicher Demo-Modus (/demo): ohne Login, ohne Speichern -->
  <DemoView v-if="isDemoRoute" />

  <!-- Ladekreis, während geprüft wird ob der User eingeloggt ist -->
  <LoadingView v-else-if="isLoading" />

  <!-- Zeige LoginView, wenn nicht eingeloggt und nicht shared view -->
  <LoginView v-else-if="!isLoggedIn && !isSharedView" @login-success="onLoginSuccess" />

  <div v-else class="min-h-screen bg-primary text-text-main font-sans">
    <div
      class="grid h-screen grid-rows-[64px_minmax(0,1fr)] overflow-hidden transition-all duration-300 ease-in-out"
      :class="gridColsClass"
      :style="{ '--rp-width': panelWidth + 'px' }"
    >
      <TopBar
        v-model:run-state="runState"
        v-model:speed-val="speedVal"
        :theme="theme"
        :is-fullscreen="isFullscreen"
        :is-debugging="isDebugging"
        @toggle-theme="toggleTheme"
        @toggle-fullscreen="toggleFullscreen"
        @open-profile="isProfileOpen = true"
        @toggle-debug-panel="isDebugging = $event"
        @toggle-debug-panel-preserve-sidebars="handleDebugPreserve"
        @undo="handleIdeAction('keyboard.undo')"
        @redo="handleIdeAction('keyboard.redo')"
        @reset="handleReset"
        @step-into="handleIdeAction('interpreter.stepInto')"
        @step-over="handleIdeAction('interpreter.stepOver')"
        @step-out="handleIdeAction('interpreter.stepOut')"
        @save="handleManualSave"
      />
      <LeftSidebar
        v-model:active-file="activeFile"
        :root="sidebarRoot"
        :username="currentUser?.name ?? 'Nutzer'"
        :read-only="isSharedView"
        :class="{ 'w-0 overflow-hidden border-r-0 p-0': !showLeftSidebar }"
        class="transition-all duration-300 ease-in-out"
        @open-new-file="isNewFileOpen = true"
        @rename-file="onRenameFile"
        @delete-file="onDeleteFile"
        @move-file="onMoveFile"
        @change-version="handleChangeVersion"
        @view-history="handleViewHistory"
        @change-permissions="handleChangePermissions"
        @show-submissions="handleShowSubmissions"
        @share-project="onShareProject"
        @delete-project="onDeleteProject"
        @rename-project="onRenameProject"
        @rename-folder="onRenameFolder"
        @delete-folder="onDeleteFolder"
      />
      <EditorArea
        ref="editorAreaRef"
        :key="ideReloadKey"
        v-model:run-state="runState"
        v-model:speed-val="speedVal"
        :active-file="activeFilePath"
        :files="ideFiles"
        :config="ideConfig"
        :font-size="fontSize"
        :theme="theme"
        :is-debugging="isDebugging"
        :show-left-sidebar="showLeftSidebar"
        :show-right-sidebar="showRightSidebar"
        :show-bottom-panel="showBottomPanel"
        @change="onIdeChange"
        @compile="onIdeCompile"
        @error="onIdeError"
        @zoom-in="zoomIn"
        @zoom-out="zoomOut"
        @toggle-left-sidebar="toggleLeftSidebar"
        @toggle-right-sidebar="toggleRightSidebar"
        @toggle-bottom-panel="toggleBottomPanel"
        @toggle-debug-panel="isDebugging = $event"
        @toggle-fullscreen="toggleFullscreen"
      />
      <!-- Right Sidebar -->
      <RightPanel
        :class="{ 'w-0 overflow-hidden border-l-0 p-0': !showRightSidebar }"
        class="transition-all duration-300 ease-in-out"
        @resize="panelWidth = $event"
      />
    </div>

    <!-- Overlays -->
    <ProfileOverlay
      :is-open="isProfileOpen"
      :current-font-size="fontSize"
      @close="isProfileOpen = false"
      @open-groups="openGroups"
      @update:font-size="fontSize = $event"
    />
    <GroupsOverlay
      :is-open="isGroupsOpen"
      @close="isGroupsOpen = false"
    />
    <NewFileOverlay
      :is-open="isNewFileOpen"
      :projects="projectOptions"
      :default-project-id="activeProjectId"
      @close="isNewFileOpen = false"
      @save="onCreateNewFile"
    />
    <ResetOverlay
      :is-open="isResetOpen"
      @close="isResetOpen = false"
      @confirm="handleResetConfirm"
    />
    <DeleteFileOverlay
      :is-open="isDeleteProjectOpen"
      :file="sidebarRoot?.folders.find((f) => f.id === deleteProjectId)?.name ?? ''"
      title="Projekt löschen"
      @close="isDeleteProjectOpen = false"
      @confirm="onDeleteProjectConfirm"
    />
    <ShareProjectOverlay
      :is-open="isShareOpen"
      :project-id="shareProject?.id ?? ''"
      :project-title="shareProject?.title ?? ''"
      :is-public="shareProject?.isPublic ?? false"
      @close="isShareOpen = false"
      @confirm="onShareConfirm"
    />
    <ForkProjectOverlay
      :is-open="forkPrompt !== null"
      :mode="forkPrompt ?? 'anonymous'"
      :project-title="shareProject?.title ?? ''"
      @close="forkPrompt = null"
      @confirm="onForkConfirm"
    />
    <OverwriteProjectOverlay
      :is-open="overwriteTarget !== null"
      :project-title="overwriteTarget?.title ?? ''"
      @close="overwriteTarget = null"
      @confirm="onOverwriteConfirm"
    />
    <ProjectNotFoundOverlay :is-open="projectNotFound" @close="onProjectNotFoundDismiss" />
    <SharedLinkOverlay
      :is-open="isSharedLinkOpen"
      :url="sharedLinkUrl"
      @close="isSharedLinkOpen = false"
      @copy="onSharedLinkCopy"
    />
    <ToastContainer />
    <VersionsOverlay
      :is-open="isVersionsOpen"
      :versions="versionsList"
      :project-name="versionsProjectName"
      :project-id="versionsProjectId"
      @close="isVersionsOpen = false"
      @switch="onVersionSwitch"
    />
    <PermissionsOverlay
      :is-open="isPermissionsOpen"
      :project-name="permProjectName"
      :current-visibility="permVisibility"
      @close="isPermissionsOpen = false"
      @save="onPermissionsSave"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch, onMounted, onUnmounted } from 'vue'
import LoginView from './views/LoginView.vue'
import LoadingView from './views/LoadingView.vue'
import DemoView from './views/DemoView.vue'
import TopBar from './components/TopBar.vue'
import LeftSidebar from './components/LeftSidebar.vue'
import EditorArea from './components/EditorArea.vue'
import RightPanel from './components/RightPanel.vue'
import ProfileOverlay from './components/overlays/ProfileOverlay.vue'
import GroupsOverlay from './components/overlays/GroupsOverlay.vue'
import NewFileOverlay from './components/overlays/NewFileOverlay.vue'
import ResetOverlay from './components/overlays/ResetOverlay.vue'
import DeleteFileOverlay from './components/overlays/DeleteFileOverlay.vue'
import ProjectNotFoundOverlay from './components/overlays/ProjectNotFoundOverlay.vue'
import ShareProjectOverlay from './components/overlays/ShareProjectOverlay.vue'
import ForkProjectOverlay from './components/overlays/ForkProjectOverlay.vue'
import OverwriteProjectOverlay from './components/overlays/OverwriteProjectOverlay.vue'
import SharedLinkOverlay from './components/overlays/SharedLinkOverlay.vue'
import ToastContainer from './components/ToastContainer.vue'
import { pushToast } from './composables/useToast'
import { copyTextToClipboard } from './online-ide/tools/HtmlTools'
import VersionsOverlay from './components/overlays/VersionsOverlay.vue'
import PermissionsOverlay from './components/overlays/PermissionsOverlay.vue'
import type { IdeFile, IdeConfig } from './components/IdeWrapper.vue'
import { API_BASE_URL } from './config'
import {
  useTheme,
  currentThemesRef as _currentThemesRef,
  getSavedThemeId,
  currentThemeIdRef,
  setActiveThemeMode,
} from './composables/useThemeNew'
import { ProjectAlreadyForkedError, ProjectService } from './services/projectService'
import { fileUid, uidProjectId, uidPath } from './services/projectService'
import type { TreeFile, TreeFolder, VersionData } from './services/projectService'
import {
  BACKEND_FALLBACK_TIMEOUT_MS,
  buildBackendFallbackTree,
} from './services/backendFallback'

/** Unified Theme-Funktion — wird von BEIDEN Quellen aufgerufen (Pill Toggle + ThemeShowcase Select).
 * Delegiert CSS-Variablen und localStorage-Sync an setActiveThemeMode() aus useThemeNew.ts,
 * aktualisiert nur die Pill-Position hier. */
function setThemeFromId(themeId: string): void {
  const mode = themeId.startsWith('1') ? 'dark' : 'light'

  // Delegiere an den zentralen Theme-Setter (CSS + localStorage + Event)
  setActiveThemeMode(mode)

  // Pill-Position aktualisieren (nur wenn sich der Mode geändert hat)
  if (mode !== theme.value) {
   // console.log(`[App] Theme-Mode gewechselt: ${theme.value} → ${mode}`)
    theme.value = mode
  }
}

/** Toggle den Dark-/Light-Slider (TopBar klickt hier).
 * Nutzt IMMER erst das Theme aus localStorage per-mode keys, falls vorhanden. */
function toggleTheme(): void {
  const nextMode = theme.value === 'dark' ? 'light' : 'dark'

  // Zuerst localStorage per-mode key prüfen (identisch mit setActiveThemeMode())
  const storageKey = nextMode === 'dark' ? 'selected-dark-theme-id' : 'selected-light-theme-id'
  let nextId = localStorage.getItem(storageKey) ?? ''

  if (!nextId || !/^[01]\d{5}$/.test(nextId)) {
    // Fallback: erst aus Themes-Liste, dann hardcoded
    const fallbackFromList = _currentThemesRef.value.find(
      (t) => t.mode === nextMode && /^[01]\d{5}$/.test(t.id)
    )?.id
    nextId = fallbackFromList ?? (nextMode === 'dark' ? '100001' : '000001')
  }

  setThemeFromId(nextId) // Delegiert CSS + localStorage + Event an setActiveThemeMode()
}

// ============================================================================
// Types
// ============================================================================

export interface User {
  id: string
  type: string
  name: string
  surname: string
  email: string
  oauth_provider: string
  oauth_subject: string
}

// ============================================================================
// Authentication
// ============================================================================

const isLoggedIn = ref(false)
const isLoading = ref(false)
const currentUser = ref<User | null>(null)
const isSharedView = ref(false)
const sharedProjectId = ref<string | null>(null)

/** True, wenn die Seite über den öffentlichen Demo-Link /demo aufgerufen wurde.
 *  In diesem Fall wird ausschließlich die DemoView gerendert (kein Login-/Projekt-Flow). */
const isDemoRoute = window.location.pathname.replace(/\/+$/, '') === '/demo'

// Prüft, ob der User eine aktive Session hat
async function checkLoginStatus() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
      credentials: 'include',
    })
    if (response.ok) {
      currentUser.value = await response.json()
      isLoggedIn.value = true
    } else {
      isLoggedIn.value = false
      currentUser.value = null
    }
  } catch (error) {
    console.error('Login-Check fehlgeschlagen:', error)
    isLoggedIn.value = false
    currentUser.value = null
  } finally {
    isLoading.value = false
  }
}

/** Handler for login-success event — checks status then loads sidebar if logged in. */
async function onLoginSuccess() {
  await checkLoginStatus()
  if (isLoggedIn.value) {
    // If an anonymous edit session left a pending fork, materialize it
    // (forks + applies the stashed edits); otherwise load the sidebar normally.
    startBackendFallbackTimer()
    if (!(await consumePendingFork())) {
      await loadSidebar()
    }
  }
}

let autosaveTimer: ReturnType<typeof setInterval> | null = null
let autosaveDebounce: ReturnType<typeof setTimeout> | null = null
let backendFallbackTimer: ReturnType<typeof setTimeout> | null = null
const AUTOSAVE_DEBOUNCE_MS = 3000

/** True when in-memory edits haven't been persisted since the last autosave.
 *  Gates the autosave (skip when clean) and the beforeunload close-block. */
const isDirty = ref(false)
/** Bumped on every keystroke so an in-flight save can tell whether new edits
 *  landed during its network round-trip (race-safe dirty clearing). */
const editCounter = ref(0)

onMounted(async () => {
  // Im Demo-Modus übernimmt DemoView die komplette Initialisierung.
  // Kein Login-Check, kein Autosave, kein Sidebar-Laden.
  if (isDemoRoute) return

  // Theme-System VOR Login prüfen (verhindert Race Condition: data-mode muss bei Kinder-Mount gesetzt sein)
  const { init } = useTheme()
  await init()
  await checkLoginStatus()

  // Lade gespeichertes Theme vom Backend (mit localStorage-Fallback)
  const initialId = await getSavedThemeId()
  currentThemeIdRef.value = initialId
  setThemeFromId(initialId)

  // Lade gespeicherte font_size aus localStorage (wurde von getSavedThemeId() aus DB befüllt)
  const savedFontSize = localStorage.getItem('fontSize')
  if (savedFontSize && parseInt(savedFontSize) >= 1) {
    fontSize.value = parseInt(savedFontSize)
  }

  // Check for shared project query param
  const params = new URLSearchParams(window.location.search)
  const pid = params.get('project')
  // Reject malformed ids — a bad shape always 404s, so let the app load normally.
  if (pid && /^[0-9a-fA-F-]{8,}$/.test(pid)) {
    sharedProjectId.value = pid
    isSharedView.value = true
  }

  // Watcher: font_size bei Änderung im Backend persistieren (debounced)
  let fontSaveTimer: ReturnType<typeof setTimeout> | null = null
  watch(fontSize, (newSize) => {
    if (fontSaveTimer) clearTimeout(fontSaveTimer)
    fontSaveTimer = setTimeout(async () => {
      try {
        await fetch(`${API_BASE_URL}/api/users/settings`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ font_size: newSize }),
        })
      } catch (error) {
        console.warn('[App] Could not save font size to backend:', error)
      }
    }, 1000)
  })

  // Externe Theme-Änderungen abonnieren (von ThemeShowcase select events + setActiveThemeMode mode changed)
  const handleThemeSelect = (event: Event) => {
    const detail = (event as CustomEvent).detail
    if (detail && /^[01]\d{5}$/.test(detail.id)) {
      setThemeFromId(detail.id)
    }
  }
  window.addEventListener('themeSelect', handleThemeSelect)

  // themeModeChanged: synchronisiert App.vue's theme ref mit setActiveThemeMode() aus useThemeNew.ts
  const handleThemeModeChanged = (event: Event) => {
    const mode = (event as CustomEvent).detail?.mode
    if (mode && mode !== theme.value) {
      // console.log(`[App] themeModeChanged: ${theme.value} → ${mode}`)
      theme.value = mode
    }
  }
  window.addEventListener('themeModeChanged', handleThemeModeChanged)

  onUnmounted(() => {
    window.removeEventListener('themeSelect', handleThemeSelect)
    window.removeEventListener('themeModeChanged', handleThemeModeChanged)
  })

  autosaveTimer = setInterval(handleAutosave, 60000) // autosave every 1 min
  window.addEventListener('beforeunload', handleBeforeUnload)

  // Responsive sidebar toggle (< 1024px = collapse) — listen for resizes dynamically
  window.addEventListener('resize', handleResize, { passive: true })

  // Load sidebar tree or shared project after login check.
  // A returning user with a stashed anonymous fork (post OAuth-redirect) takes
  // precedence: fork + apply edits, then show the user's namespace.
  if (isLoggedIn.value && localStorage.getItem(PENDING_FORK_KEY)) {
    startBackendFallbackTimer()
    await consumePendingFork()
  } else if (isSharedView.value) {
    // If the logged-in user owns the shared project, redirect to normal workspace.
    if (await resolveSharedViewForLoggedInUser()) return
    await loadSharedProject(sharedProjectId.value!)
  } else if (isLoggedIn.value) {
    startBackendFallbackTimer()
    await loadSidebar()
  }
})

onUnmounted(() => {
  if (autosaveTimer) clearInterval(autosaveTimer)
  if (autosaveDebounce) clearTimeout(autosaveDebounce)
  clearBackendFallbackTimer()
  window.removeEventListener('beforeunload', handleBeforeUnload)
  window.removeEventListener('resize', handleResize)
})

// ============================================================================
// Sidebar Tree State
// ============================================================================

const sidebarRoot = ref<TreeFolder | null>(null)
const fileStore = reactive(new Map<string, string>())

/** Loads the sidebar tree from the API and seeds fileStore. */
async function loadSidebar() {
  try {
    const { root, contents } = await ProjectService.loadSidebarTree()
    fileStore.clear()
    // `contents` is keyed by file uid (projectId + path), which is also how the
    // fileStore is keyed, so same-named files across projects stay independent.
    for (const [uid, content] of Object.entries(contents)) {
      fileStore.set(uid, content)
    }
    sidebarRoot.value = root
    activeFile.value = firstFileUid(root)
    isDirty.value = false
    clearBackendFallbackTimer()
  } catch (e) {
    console.error('[App] Failed to load sidebar:', e)
  }
}

function clearBackendFallbackTimer() {
  if (backendFallbackTimer) {
    clearTimeout(backendFallbackTimer)
    backendFallbackTimer = null
  }
}

function startBackendFallbackTimer() {
  if (backendFallbackTimer || sidebarRoot.value) {
    return
  }

  backendFallbackTimer = setTimeout(() => {
    backendFallbackTimer = null

    if (sidebarRoot.value) {
      return
    }

    const { root, contents } = buildBackendFallbackTree()
    fileStore.clear()
    for (const [uid, content] of Object.entries(contents)) {
      fileStore.set(uid, content)
    }
    sidebarRoot.value = root
    activeFile.value = firstFileUid(root)
    isDirty.value = false
  }, BACKEND_FALLBACK_TIMEOUT_MS)
}

/**
 * For a logged-in user viewing a shared link: checks whether they own the project.
 * Returns true if handled-as-owner (set isSharedView=false, loaded own workspace).
 * Returns false if not owner — caller should load as shared view.
 */
async function resolveSharedViewForLoggedInUser(): Promise<boolean> {
  if (!isLoggedIn.value || !sharedProjectId.value) return false
  try {
    const [shared, nsRes] = await Promise.all([
      ProjectService.getProject(sharedProjectId.value),
      fetch(`${API_BASE_URL}/api/users/namespace`, { credentials: 'include' }),
    ])
    if (!nsRes.ok) return false
    const ns = (await nsRes.json()) as { id: string }
    if (ns?.id && shared?.namespace_id && ns.id === shared.namespace_id) {
      // Owner — treat as normal workspace, not a shared view.
      exitSharedView()
      startBackendFallbackTimer()
      await loadSidebar()
      return true
    }
  } catch {
    // Network/parse errors: fall through to shared view loading.
  }
  return false
}

/** Loads a shared project by ID and seeds fileStore for anonymous viewing. */
async function loadSharedProject(projectId: string) {
  try {
    const p = await ProjectService.getProject(projectId)
    const files = Array.isArray(p.data) ? p.data : []
    const projectFolder = ProjectService.buildProjectTree(p.id, p.title ?? p.name ?? p.id, files)
    const root: TreeFolder = {
      id: 'shared-root',
      name: 'Geteilt',
      folders: [projectFolder],
      files: [],
    }
    fileStore.clear()
    for (const f of files) {
      fileStore.set(fileUid(p.id, f.path), f.content ?? '')
    }
    sidebarRoot.value = root
    activeFile.value = firstFileUid(root)
    isDirty.value = false
  } catch (e) {
    console.error('[App] Failed to load shared project', e)
    // Project deleted / inaccessible: show a popup instead of an empty shared
    // view, then route to login (or the logged-in workspace).
    projectNotFound.value = true
  }
}

/** Leave the shared view after a deleted/inaccessible project popup. */
function onProjectNotFoundDismiss() {
  projectNotFound.value = false
  exitSharedView()
  // Anonymous -> LoginView reveals automatically; logged-in -> own workspace.
  if (isLoggedIn.value) {
    startBackendFallbackTimer()
    loadSidebar()
  }
}

/** Returns the first file path within a single project subtree (DFS), or ''. */
function firstFilePathIn(folder: TreeFolder): string {
  if (folder.files.length > 0) return folder.files[0].path
  for (const sub of folder.folders) {
    const result = firstFilePathIn(sub)
    if (result) return result
  }
  return ''
}

/** Returns the uid of the first file in the tree (DFS over projects), or ''.
 *  The namespace root's direct children are the projects, so each file's uid is
 *  resolved against its owning top-level project. */
function firstFileUid(root: TreeFolder | null): string {
  if (!root) return ''
  for (const project of root.folders) {
    const path = firstFilePathIn(project)
    if (path) return fileUid(project.id, path)
  }
  return ''
}

/** Returns all files in a folder subtree. */
function flattenFiles(folder: TreeFolder): TreeFile[] {
  const result: TreeFile[] = [...folder.files]
  for (const sub of folder.folders) {
    result.push(...flattenFiles(sub))
  }
  return result
}

/** Finds a folder node anywhere in the subtree by its id. */
function findFolderById(folder: TreeFolder | null, id: string): TreeFolder | null {
  if (!folder) return null
  if (folder.id === id) return folder
  for (const sub of folder.folders) {
    const found = findFolderById(sub, id)
    if (found) return found
  }
  return null
}

/** Removes the folder node with the given id from its parent. */
function removeFolderById(root: TreeFolder | null, id: string): boolean {
  if (!root) return false
  const idx = root.folders.findIndex(f => f.id === id)
  if (idx !== -1) {
    root.folders.splice(idx, 1)
    return true
  }
  for (const sub of root.folders) {
    if (removeFolderById(sub, id)) return true
  }
  return false
}

/**
 * Re-prefixes a folder subtree after a folder rename: rewrites the folder ids
 * (which embed the path) and the file paths (plus their fileStore keys) so the
 * in-memory tree matches the new prefix without a full reload.
 */
function reprefixSubtree(
  folder: TreeFolder,
  projectId: string,
  oldFolderId: string,
  newFolderId: string,
  oldPrefix: string,
  newPrefix: string
) {
  if (folder.id === oldFolderId) {
    folder.id = newFolderId
  } else if (folder.id.startsWith(`${oldFolderId}/`)) {
    folder.id = newFolderId + folder.id.slice(oldFolderId.length)
  }
  for (const file of folder.files) {
    if (file.path.startsWith(`${oldPrefix}/`)) {
      const newPath = newPrefix + file.path.slice(oldPrefix.length)
      const oldUid = fileUid(projectId, file.path)
      const newUid = fileUid(projectId, newPath)
      if (fileStore.has(oldUid)) {
        fileStore.set(newUid, fileStore.get(oldUid)!)
        fileStore.delete(oldUid)
      }
      if (activeFile.value === oldUid) activeFile.value = newUid
      file.path = newPath
    }
  }
  for (const sub of folder.folders) {
    reprefixSubtree(sub, projectId, oldFolderId, newFolderId, oldPrefix, newPrefix)
  }
}

// The currently selected file, identified by its uid (projectId + path) — never
// a bare path — so selection, project resolution and content lookups stay
// scoped to the one project the file belongs to even when another project holds
// a file with the same path.
const activeFile = ref('')

/** The project subtree that owns the active file (resolved by id, not by scanning
 *  for a matching path — which would pick the wrong project on a name clash). */
const activeProject = computed(() =>
  findFolderById(sidebarRoot.value, uidProjectId(activeFile.value))
)

/** The active file's plain path — what the embedded IDE addresses files by. */
const activeFilePath = computed(() => uidPath(activeFile.value))

const runState = ref<'stopped' | 'running' | 'paused'>('stopped')
const speedVal = ref(100)
const panelWidth = ref(320)
const editorAreaRef = ref<InstanceType<typeof EditorArea> | null>(null)

// ============================================================================
// Theme, Fullscreen, and Zoom States
// ============================================================================

/** Slider-Modus ausschließlich aus der unified Methode bestimmen. */
function _initialThemeMode(): 'light' | 'dark' {
  // 1. Per-mode Keys priorisieren (explizite user choice)
  const dark = localStorage.getItem('selected-dark-theme-id')
  const light = localStorage.getItem('selected-light-theme-id')
  if (dark && /^[01]\d{5}$/.test(dark)) return 'dark'
  if (light && /^[01]\d{5}$/.test(light)) return 'light'
  // 2. themes.json default: ID-Prefix prüft Mode (gleicher Weg wie useThemeNew.ts initThemes)
  const firstId = _currentThemesRef.value[0]?.id ?? '100001'
  if (/^[01]/.test(firstId)) return firstId.startsWith('1') ? 'dark' : 'light'
  // 3. Fallback: dark (entspricht useThemeNew.ts default)
  return 'light'  // entspricht FALLBACK_DEFAULT_THEME_ID = '000001' (Light) in useThemeNew.ts
}

const theme = ref<'light' | 'dark'>(_initialThemeMode())

const initialSidebarState = typeof window !== 'undefined' ? window.innerWidth >= 1024 : false

const showLeftSidebar = ref(initialSidebarState)
const showRightSidebar = ref(initialSidebarState)
const showBottomPanel = ref(true)

/** Automatically collapse sidebars when screen < 1024px and expand when >= 1024px. When debugging is active, do not auto-trigger fullscreen. */
function handleResize() {
  const collapsed = window.innerWidth < 1024
  // Don't auto-trigger fullscreen while debugging — keep current sidebar state
  if (isDebugging.value) return
  showLeftSidebar.value = !collapsed
  showRightSidebar.value = !collapsed
}
const isDebugging = ref(false)
const fontSize = ref(14)
// Overlays & Modal States
const isProfileOpen = ref(false)
const isGroupsOpen = ref(false)
const isNewFileOpen = ref(false)
const isResetOpen = ref(false)
const deleteProjectId = ref<string | null>(null)
const isDeleteProjectOpen = ref(false)
const projectNotFound = ref(false)
const isShareOpen = ref(false)
const shareProject = ref<{ id: string; title: string; isPublic: boolean } | null>(null)
const sharedLinkUrl = ref('')
const isSharedLinkOpen = ref(false)
const forkPrompt = ref<null | 'logged-in' | 'anonymous'>(null)
const overwriteTarget = ref<{ id: string; title: string } | null>(null)
const overwriteFiles = ref<{ path: string; content: string }[]>([])

// Version history overlay state
const isVersionsOpen = ref(false)
const versionsList = ref<VersionData[]>([])
const versionsProjectId = ref<string | null>(null)
const versionsProjectName = ref<string>('')

// Bumped to force the editor to remount. A version switch replaces file *content*
// but usually keeps the same file *names*, and the IDE skips content-only updates
// (it re-inits only when the file-name set changes), so switching would otherwise
// leave stale content on screen.
const ideReloadKey = ref(0)

// Permissions (project visibility) overlay state
const isPermissionsOpen = ref(false)
const permProjectId = ref<string | null>(null)
const permProjectName = ref<string>('')
const permVisibility = ref<'private' | 'internal' | 'public'>('private')

function openGroups() {
  isProfileOpen.value = false
  isGroupsOpen.value = true
}

const activeProjectId = computed<string | null>(() => activeProject.value?.id ?? null)

/** Flat project list {id, name} for the NewFileOverlay project selector. */
const projectOptions = computed(() =>
  (sidebarRoot.value?.folders ?? []).map((p) => ({ id: p.id, name: p.name }))
)

// When debug panel opens, collapse sidebars; restore when closed.
const prevLeftPinned = ref(showLeftSidebar.value)
const prevRightPinned = ref(showRightSidebar.value)
const prevBottomPinned = ref(showBottomPanel.value)
// When a debug toggle originates from the TopBar "Geschwindigkeit & Debug" button
// we may want to preserve the sidebar pinned state. This flag skips the collapse
// logic for one isDebugging change when set by handleDebugPreserve().
const skipDebugCollapse = ref(false)
watch(
() => isDebugging.value,
(val) => {
  // Respect explicit preserve requests from TopBar
  if (skipDebugCollapse.value) {
    skipDebugCollapse.value = false
    return
  }

  if (val) {
    // Save previous pinned state
    prevLeftPinned.value = showLeftSidebar.value
    prevRightPinned.value = showRightSidebar.value
    prevBottomPinned.value = showBottomPanel.value
    // Collapse both sidebars while debugging is active, but avoid forcing
    // fullscreen on small screens (<1024). This prevents debug from triggering
    // the fullscreen behavior.
    if (window.innerWidth >= 1024) {
      showLeftSidebar.value = false
      showRightSidebar.value = false
    }
    // Open bottom panel (console) for debugging
    showBottomPanel.value = true
  } else {
    showLeftSidebar.value = prevLeftPinned.value
    showRightSidebar.value = prevRightPinned.value
    // Restore bottom panel to previous state
    showBottomPanel.value = prevBottomPinned.value
  }
}
)

async function handleManualSave() {
  if (isSharedView.value) {
    forkPrompt.value = isLoggedIn.value ? 'logged-in' : 'anonymous'
    return
  }
  const pId = activeProjectId.value
  if (!pId) return
  const project = activeProject.value
  const data = project
    ? flattenFiles(project).map((f) => ({ path: f.path, content: fileStore.get(fileUid(project.id, f.path)) ?? '' }))
    : undefined
  try {
    await ProjectService.saveProject(pId, 'Manual save', data)
    isDirty.value = false
    console.log('Project saved manually')
  } catch (e) {
    console.error('Failed to save project', e)
  }
}

async function handleAutosave() {
  if (isSharedView.value) return
  if (!isDirty.value) return // nothing changed since the last save
  const pId = activeProjectId.value
  if (!pId) return
  const before = editCounter.value
  const data = activeProjectFiles()
  try {
    await ProjectService.autosaveProject(pId, data)
    console.log('Project autosaved')
  } catch (e) {
    console.error('Failed to autosave project', e)
    return // keep dirty → next tick retries
  }
  // Only clear dirty if no keystrokes landed during the round-trip.
  if (editCounter.value === before) isDirty.value = false
}

/** Collect the active project's current files for an autosave. Returns undefined
 *  when there is no active project. */
function activeProjectFiles(): { path: string; content: string }[] | undefined {
  const project = activeProject.value
  return project
    ? flattenFiles(project).map((f) => ({ path: f.path, content: fileStore.get(fileUid(project.id, f.path)) ?? '' }))
    : undefined
}

/** Reset the typing-debounce timer; fire an autosave AUTOSAVE_DEBOUNCE_MS after
 *  the last keystroke. */
function scheduleDebouncedAutosave() {
  if (autosaveDebounce) clearTimeout(autosaveDebounce)
  autosaveDebounce = setTimeout(() => {
    autosaveDebounce = null
    void handleAutosave()
  }, AUTOSAVE_DEBOUNCE_MS)
}

/** Best-effort final autosave on tab close. keepalive lets the request finish
 *  after navigation (payload cap ~64KB; the native confirm still lets the user
 *  stay and save manually for very large projects). */
async function flushAutosaveKeepalive() {
  const pId = activeProjectId.value
  if (!pId) return
  try {
    await ProjectService.autosaveProject(pId, activeProjectFiles(), { keepalive: true })
  } catch {
    /* best-effort on unload */
  }
}

function handleBeforeUnload(event: BeforeUnloadEvent) {
  if (!isDirty.value || isSharedView.value) return
  void flushAutosaveKeepalive()
  event.preventDefault()
  event.returnValue = ''
}

// Cancel a pending debounce when the active project changes so a save never
// targets the wrong project after a quick switch. Edits stay in fileStore.
// ponytail: per-project dirty tracking would flush the previous project too;
// out of scope — switching away defers its save until revisited.
watch(activeProjectId, () => {
  if (autosaveDebounce) {
    clearTimeout(autosaveDebounce)
    autosaveDebounce = null
  }
})

/** Opens the versions overlay for the project with the given id. Version history
 *  is a project-level concept, so both the project menu ("Version ändern") and
 *  a file's "Historie anzeigen" resolve to their owning project id first. */
async function openVersionsForProject(projectId: string) {
  const proj = findFolderById(sidebarRoot.value, projectId)
  if (!proj) return
  versionsProjectId.value = proj.id
  versionsProjectName.value = proj.name
  versionsList.value = []
  isVersionsOpen.value = true
  try {
    const res = await ProjectService.getVersionHistory(proj.id)
    versionsList.value = res.versions ?? []
  } catch (e) {
    console.error('Failed to fetch versions', e)
    alert('Fehler beim Laden der Versionen.')
  }
}

/** Loads a past version's file contents into the editor for the active project. */
async function onVersionSwitch(versionId: string) {
  const projectId = versionsProjectId.value
  if (!projectId) return
  try {
    const project = await ProjectService.getProject(projectId, versionId)
    const targetFolder = sidebarRoot.value?.folders.find(f => f.id === projectId)
    const files = Array.isArray(project.data) ? project.data : []
    // Replace the project's file contents + tree entries with the chosen version.
    for (const file of files) {
      fileStore.set(fileUid(projectId, file.path), file.content ?? '')
    }
    if (targetFolder) {
      targetFolder.files = files
        .filter(f => !f.path.includes('/'))
        .map(f => ({ path: f.path, name: f.path }))
      targetFolder.folders = []
      for (const file of files) {
        if (file.path.includes('/')) {
          // Rebuild nested folders for this version's paths.
          const segments = file.path.split('/')
          let current = targetFolder
          for (let i = 0; i < segments.length - 1; i++) {
            const folderId = `${projectId}/${segments.slice(0, i + 1).join('/')}`
            let next = current.folders.find(fo => fo.id === folderId)
            if (!next) {
              next = { id: folderId, name: segments[i], folders: [], files: [] }
              current.folders.push(next)
            }
            current = next
          }
          current.files.push({ path: file.path, name: segments[segments.length - 1] })
        }
      }
    }

    // Keep a valid active file if the current one doesn't exist in this version.
    if (!files.some(f => fileUid(projectId, f.path) === activeFile.value)) {
      activeFile.value = files[0] ? fileUid(projectId, files[0].path) : ''
    }

    // Persist the restored state so the switch is permanent (survives a reload)
    // and is recorded as a new entry in the version history.
    await ProjectService.saveProject(
      projectId,
      'Version wiederhergestellt',
      files.map(f => ({ path: f.path, content: f.content ?? '' }))
    )

    // A version switch changes file content but usually not the file names, so
    // force the editor to remount and pick up the restored content.
    ideReloadKey.value++
  } catch (e) {
    console.error('Failed to switch version', e)
    alert('Die Version konnte nicht geladen werden.')
  } finally {
    isVersionsOpen.value = false
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function handleUpdateFromParent() {
  const pId = activeProjectId.value
  if (!pId) return
  const parentId = prompt('Geben Sie die ID des übergeordneten Projekts (Parent) ein:')
  if (!parentId) return
  try {
    await ProjectService.updateFromParent(pId, parentId)
    console.log('Project updated from parent')
  } catch (e) {
    console.error('Failed to update from parent', e)
  }
}

const isFullscreen = computed(() => !showLeftSidebar.value && !showRightSidebar.value)

const gridColsClass = computed(() => {
  if (showLeftSidebar.value && showRightSidebar.value) {
    return 'grid-cols-[280px_minmax(0,1fr)_var(--rp-width,320px)]'
  } else if (!showLeftSidebar.value && showRightSidebar.value) {
    return 'grid-cols-[0px_minmax(0,1fr)_var(--rp-width,320px)]'
  } else if (showLeftSidebar.value && !showRightSidebar.value) {
    return 'grid-cols-[280px_minmax(0,1fr)_0px]'
  } else {
    return 'grid-cols-[0px_minmax(0,1fr)_0px]'
  }
})

function toggleLeftSidebar() {
  showLeftSidebar.value = !showLeftSidebar.value
}

function toggleRightSidebar() {
  showRightSidebar.value = !showRightSidebar.value
}

function toggleBottomPanel() {
  showBottomPanel.value = !showBottomPanel.value
}

function toggleFullscreen() {
  if (isFullscreen.value) {
    showLeftSidebar.value = true
    showRightSidebar.value = true
  } else {
    showLeftSidebar.value = false
    showRightSidebar.value = false
  }
}

function handleDebugPreserve(val: boolean) {
  // Called when the TopBar's "Geschwindigkeit & Debug" button toggles the
  // debug panel but the sidebar pinned state should be preserved.
  skipDebugCollapse.value = true
  isDebugging.value = val
}

function zoomIn() {
  fontSize.value = Math.min(80, Math.round(fontSize.value * 1.1))
}

function zoomOut() {
  fontSize.value = Math.max(6, Math.round(fontSize.value * 0.9))
}

// ============================================================================
// File Operations (path-based)
// ============================================================================

async function onRenameFile(uid: string, newName: string) {
  // The file is identified by its uid; resolve the owning project by id so a
  // same-named file in another project is never touched.
  const projectId = uidProjectId(uid)
  const oldPath = uidPath(uid)
  // The overlay edits only the basename, so the file stays in its folder.
  const prefix = oldPath.includes('/') ? oldPath.slice(0, oldPath.lastIndexOf('/')) : ''
  const newPath = prefix ? `${prefix}/${newName}` : newName
  if (!newName || newPath === oldPath) return

  const proj = findFolderById(sidebarRoot.value, projectId)
  const fileEntry = proj ? flattenFiles(proj).find(f => f.path === oldPath) : null

  // Reject duplicates before touching anything, so we never clobber a sibling.
  if (proj && flattenFiles(proj).some(f => f.path === newPath)) {
    alert('Eine Datei mit diesem Namen existiert bereits im Ordner!')
    return
  }

  const oldName = oldPath.split('/').pop() ?? oldPath
  const newUid = fileUid(projectId, newPath)

  // Optimistic update: reflect the rename immediately, roll back on failure.
  if (fileEntry) {
    fileEntry.path = newPath
    fileEntry.name = newName
  }
  if (fileStore.has(uid)) {
    fileStore.set(newUid, fileStore.get(uid)!)
    fileStore.delete(uid)
  }
  if (activeFile.value === uid) activeFile.value = newUid

  if (!proj) return
  try {
    await ProjectService.renameFile(projectId, oldPath, newPath)
  } catch (e) {
    if (fileEntry) {
      fileEntry.path = oldPath
      fileEntry.name = oldName
    }
    if (fileStore.has(newUid)) {
      fileStore.set(uid, fileStore.get(newUid)!)
      fileStore.delete(newUid)
    }
    if (activeFile.value === newUid) activeFile.value = uid
    console.error('Failed to rename file', e)
    alert('Die Datei konnte nicht umbenannt werden.')
  }
}

function onDeleteFile(uid: string) {
  const projectId = uidProjectId(uid)
  const path = uidPath(uid)
  // Remove from tree — resolve the project by id so the correct copy is removed.
  const proj = findFolderById(sidebarRoot.value, projectId)
  if (proj) {
    const removeFromFolder = (folder: TreeFolder): boolean => {
      const idx = folder.files.findIndex((f) => f.path === path)
      if (idx !== -1) {
        folder.files.splice(idx, 1)
        return true
      }
      for (const sub of folder.folders) {
        if (removeFromFolder(sub)) return true
      }
      return false
    }
    removeFromFolder(proj)
  }

  fileStore.delete(uid)
  if (activeFile.value === uid) {
    activeFile.value = ''
  }

  // Best-effort API
  if (proj) {
    ProjectService.deleteFile(projectId, path).catch((e) => console.error(e))
  }
}

async function onMoveFile(uid: string, targetFolderId: string) {
  if (!targetFolderId) return
  // Resolve the source by the project encoded in the uid, not by scanning for a
  // matching path: two projects may hold a file with the same relative path, and
  // a path-only lookup could pick the wrong one (moving/duplicating a wrong copy).
  const sourceProjectId = uidProjectId(uid)
  const path = uidPath(uid)
  // The overlay's folder id encodes the destination as "<projectId>/<subpath>".
  const parts = targetFolderId.split('/')
  const targetProjectId = parts[0]
  const targetPrefix = parts.slice(1).join('/')
  const basename = path.split('/').pop() ?? path
  const newPath = targetPrefix ? `${targetPrefix}/${basename}` : basename
  const newUid = fileUid(targetProjectId, newPath)

  const srcProj = findFolderById(sidebarRoot.value, sourceProjectId)
  const targetFolder = findFolderById(sidebarRoot.value, targetFolderId)

  // Without a resolvable source project or target folder we cannot move cleanly.
  // Bail out instead of adding the file to the target while leaving the original
  // in place (which would duplicate it into both folders).
  if (!srcProj || !targetFolder) return

  // Moving a file onto its own location is a no-op.
  if (srcProj.id === targetProjectId && newPath === path) return

  // Reject duplicates in the destination before detaching from the source.
  if (targetFolder.files.some(f => f.path === newPath)) {
    alert('Eine Datei mit diesem Namen existiert bereits im Zielordner!')
    return
  }

  // Detach from the source folder FIRST, remembering where it was for rollback.
  // Only once the file is actually removed do we add it to the target, so a move
  // can never leave a copy behind in the source folder.
  let sourceFolder: TreeFolder | null = null
  const removeFromFolder = (folder: TreeFolder): boolean => {
    const idx = folder.files.findIndex(f => f.path === path)
    if (idx !== -1) {
      folder.files.splice(idx, 1)
      sourceFolder = folder
      return true
    }
    for (const sub of folder.folders) {
      if (removeFromFolder(sub)) return true
    }
    return false
  }
  if (!removeFromFolder(srcProj)) return

  targetFolder.files.push({ path: newPath, name: basename })
  if (fileStore.has(uid)) {
    fileStore.set(newUid, fileStore.get(uid)!)
    fileStore.delete(uid)
  }
  if (activeFile.value === uid) activeFile.value = newUid

  try {
    await ProjectService.moveFile(srcProj.id, path, targetProjectId, newPath)
  } catch (e) {
    // Roll the optimistic move back so a failed request leaves no phantom file.
    if (targetFolder) {
      const idx = targetFolder.files.findIndex(f => f.path === newPath)
      if (idx !== -1) targetFolder.files.splice(idx, 1)
    }
    if (sourceFolder) (sourceFolder as TreeFolder).files.push({ path, name: basename })
    if (fileStore.has(newUid)) {
      fileStore.set(uid, fileStore.get(newUid)!)
      fileStore.delete(newUid)
    }
    if (activeFile.value === newUid) activeFile.value = uid
    console.error('Failed to move file', e)
    alert('Die Datei konnte nicht verschoben werden.')
  }
}

async function onRenameFolder(folderId: string, newName: string) {
  // folderId is "<projectId>/<prefix>"; prefix is the folder path in the project.
  const projectId = folderId.split('/')[0]
  const oldPrefix = folderId.slice(projectId.length + 1)
  if (!oldPrefix || !newName) return
  const parentPrefix = oldPrefix.includes('/')
    ? oldPrefix.slice(0, oldPrefix.lastIndexOf('/'))
    : ''
  const newPrefix = parentPrefix ? `${parentPrefix}/${newName}` : newName
  if (newPrefix === oldPrefix) return

  const newFolderId = `${projectId}/${newPrefix}`
  const folderNode = findFolderById(sidebarRoot.value, folderId)
  if (!folderNode) return

  // Reject a sibling-name collision before touching anything.
  if (findFolderById(sidebarRoot.value, newFolderId)) {
    alert('Ein Ordner mit diesem Namen existiert bereits.')
    return
  }

  // Optimistic: rename the node and re-prefix its whole subtree.
  folderNode.name = newName
  reprefixSubtree(folderNode, projectId, folderId, newFolderId, oldPrefix, newPrefix)

  try {
    await ProjectService.renameFolder(projectId, oldPrefix, newPrefix)
  } catch (e) {
    // Folder renames rewrite a whole subtree; on failure the cheapest way back
    // to a consistent tree is a reload from the source of truth.
    console.error('Failed to rename folder', e)
    alert('Der Ordner konnte nicht umbenannt werden.')
    await loadSidebar()
  }
}

async function onRenameProject(projectId: string, newName: string) {
  if (!newName) return
  const folder = sidebarRoot.value?.folders.find((f) => f.id === projectId)
  if (!folder || folder.name === newName) return

  const oldName = folder.name
  folder.name = newName

  try {
    await ProjectService.updateProject(projectId, { title: newName })
  } catch (e) {
    console.error('Failed to rename project', e)
    alert('Das Projekt konnte nicht umbenannt werden.')
    folder.name = oldName
  }
}

async function onDeleteFolder(folderId: string) {
  const projectId = folderId.split('/')[0]
  const prefix = folderId.slice(projectId.length + 1)
  if (!prefix) return

  const folderNode = findFolderById(sidebarRoot.value, folderId)
  if (!folderNode) return

  // Optimistic: drop the subtree and purge its files from the content store.
  const contained = flattenFiles(folderNode)
  removeFolderById(sidebarRoot.value, folderId)
  for (const file of contained) {
    const uid = fileUid(projectId, file.path)
    fileStore.delete(uid)
    if (activeFile.value === uid) activeFile.value = ''
  }

  try {
    await ProjectService.deleteFolder(projectId, prefix)
  } catch (e) {
    console.error('Failed to delete folder', e)
    alert('Der Ordner konnte nicht gelöscht werden.')
    await loadSidebar()
  }
}

async function onCreateNewFile(data: { type: 'file' | 'project'; name: string; folderId: string }) {
  if (data.type === 'file') {
    const parts = data.folderId.split('/')
    const projectId = parts[0]
    const prefix = parts.slice(1).join('/')
    const newPath = prefix ? `${prefix}/${data.name}` : data.name

    // Find the target folder in sidebarRoot
    const findFolder = (folder: TreeFolder, fid: string): TreeFolder | null => {
      if (folder.id === fid) return folder
      for (const sub of folder.folders) {
        const result = findFolder(sub, fid)
        if (result) return result
      }
      return null
    }

    const targetFolder = sidebarRoot.value ? findFolder(sidebarRoot.value, data.folderId) : null
    if (!targetFolder) {
      alert('Zielordner nicht gefunden.')
      return
    }
    // Check for duplicate
    if (targetFolder.files.some((f) => f.path === newPath)) {
      alert('Eine Datei mit diesem Namen existiert bereits im Ordner!')
      return
    }
    const content = `// Neue Datei: ${data.name}\n`
    const newUid = fileUid(projectId, newPath)
    // Optimistic client update: show the file in the sidebar immediately and
    // persist in the background; roll back if the backend rejects the create.
    targetFolder.files.push({ path: newPath, name: data.name })
    fileStore.set(newUid, content)
    activeFile.value = newUid
    try {
      await ProjectService.createFile(projectId, newPath, content)
    } catch (e) {
      // Rollback the optimistic update so the sidebar never shows a phantom file.
      targetFolder.files = targetFolder.files.filter((f) => f.path !== newPath)
      fileStore.delete(newUid)
      if (activeFile.value === newUid) activeFile.value = ''
      console.error('Failed to create file', e)
      alert('Die Datei konnte nicht erstellt werden.')
    }
  } else if (data.type === 'project') {
    const namespaceId = sidebarRoot.value?.id
    if (!namespaceId) {
      console.error('No active namespace')
      alert('Kein aktiver Namespace gefunden.')
      return
    }
    try {
      await ProjectService.createProject({ title: data.name, namespace_id: namespaceId })
      await loadSidebar() // refresh tree so the new project appears
    } catch (e) {
      console.error('Failed to create project', e)
      alert('Projekt konnte nicht erstellt werden. Bitte versuchen Sie es erneut.')
    }
  }
}

// ============================================================================
// File Content Store
// ============================================================================

function handleReset() {
  isResetOpen.value = true
}

async function handleResetConfirm() {
  const pId = activeProjectId.value
  if (pId) {
    try {
      // getProject loads the "latest" branch = last manual save (autosaves
      // only land on the content branch, never move the latest pointer).
      const p = await ProjectService.getProject(pId)
      const files = Array.isArray(p.data) ? p.data : []
      for (const f of files) fileStore.set(fileUid(pId, f.path), f.content ?? '')
    } catch (e) {
      console.error('[App] Reset failed', e)
      pushToast('Zurücksetzen fehlgeschlagen', 'error')
    }
  }
  // The IDE watcher keys on the file-NAME signature only, so a content-only
  // change would not refresh Monaco. Bumping the key remounts the IDE with the
  // reset content (undo history is discarded — which is the point of a reset).
  ideReloadKey.value++
  isResetOpen.value = false
}

function onDeleteProject(projectId: string) {
  deleteProjectId.value = projectId
  isDeleteProjectOpen.value = true
}

async function onDeleteProjectConfirm() {
  const id = deleteProjectId.value
  if (!id) return
  // Clear activeFile before loadSidebar to avoid a stale-target window.
  if (activeProjectId.value === id) {
    activeFile.value = ''
  }
  try {
    await ProjectService.deleteProject(id)
    pushToast('Projekt gelöscht', 'success')
    await loadSidebar()
  } catch (e) {
    console.error('[App] Failed to delete project', e)
    pushToast('Löschen fehlgeschlagen', 'error')
  }
}

function handleIdeAction(action: string, payload?: unknown) {
  if (editorAreaRef.value && typeof editorAreaRef.value.triggerIdeAction === 'function') {
    editorAreaRef.value.triggerIdeAction(action, payload)
  } else {
    console.warn('[App] Cannot trigger IDE action, editorAreaRef is not available')
  }
}

// ============================================================================
// IDE Integration
// ============================================================================

// Build IdeFile[] from the active project
const ideFiles = computed<IdeFile[]>(() => {
  const proj = activeProject.value
  if (!proj) return []
  return flattenFiles(proj).map((f) => ({
    name: f.path,
    content: fileStore.get(fileUid(proj.id, f.path)) || '',
    type: 'java' as const,
  }))
})

const ideConfig = computed<IdeConfig>(() => ({
  speed: 'max',
  withFileList: false,
  withConsole: true,
  withErrorList: true,
  withPCode: false,
  withBottomPanel: showBottomPanel.value,
  hideEditor: false,
  hideStartPanel: false,
  libraries: [],
  enableFileAccess: false,
}))

// Handle file content changes from the IDE. The IDE reports the file by its
// name (= path); it only ever shows the active project's files, so the edit
// belongs to the active project and is stored under that project's uid.
function onIdeChange(fileName: string, content: string) {
  const projectId = activeProjectId.value
  if (!projectId) return
  fileStore.set(fileUid(projectId, fileName), content)
  isDirty.value = true
  editCounter.value++
  scheduleDebouncedAutosave()
}

function onIdeCompile(hasErrors: boolean) {
  console.log('[App] Compilation finished, hasErrors:', hasErrors)
}

function onIdeError(errors: unknown[]) {
  console.warn('[App] Compilation errors:', errors)
}

// ============================================================================
// Placeholder handlers for remaining LeftSidebar file actions
// ============================================================================

// The project context menu emits the project id directly.
function handleChangeVersion(projectId: string) {
  openVersionsForProject(projectId)
}

// A file's "Historie anzeigen" emits the file uid; history is project-level, so
// resolve to the owning project.
function handleViewHistory(uid: string) {
  openVersionsForProject(uidProjectId(uid))
}

async function handleChangePermissions(projectId: string) {
  // File-level ACLs do not exist in the backend; the closest real control is the
  // owning project's visibility, so this edits that. The project menu passes the
  // project id, so resolve the project by id (not by scanning file paths).
  const proj = findFolderById(sidebarRoot.value, projectId)
  if (!proj) return
  try {
    const project = await ProjectService.getProject(proj.id)
    permProjectId.value = proj.id
    permProjectName.value = proj.name
    permVisibility.value = project.visibility ?? 'private'
    isPermissionsOpen.value = true
  } catch (e) {
    console.error('Failed to load project visibility', e)
    alert('Die Sichtbarkeit konnte nicht geladen werden.')
  }
}

async function onPermissionsSave(visibility: 'private' | 'internal' | 'public') {
  const projectId = permProjectId.value
  if (!projectId) return
  try {
    await ProjectService.updateProject(projectId, { visibility })
  } catch (e) {
    console.error('Failed to update visibility', e)
    alert('Die Sichtbarkeit konnte nicht gespeichert werden.')
  }
}

async function handleShowSubmissions(uid: string) {
  // Submissions live in the exams domain and are keyed by exam, not by file.
  // Only exam projects can have any, so gate on is_exam and explain otherwise.
  const proj = findFolderById(sidebarRoot.value, uidProjectId(uid))
  if (!proj) return
  try {
    const project = await ProjectService.getProject(proj.id)
    if (!project.is_exam) {
      alert('Für diese Datei gibt es keine Abgaben (kein Prüfungsprojekt).')
      return
    }
    alert(
      'Dieses Projekt ist eine Prüfung. Das Anzeigen der Abgaben pro Datei ist noch ' +
        'nicht angebunden — Abgaben werden aktuell pro Prüfung im Prüfungsbereich verwaltet.'
    )
  } catch (e) {
    console.error('Failed to check submissions', e)
    alert('Abgaben konnten nicht geprüft werden.')
  }
}

async function onShareProject(projectId: string) {
  try {
    const p = await ProjectService.getProject(projectId)
    const isPublic = p.visibility === 'public'
    const folder = sidebarRoot.value?.folders.find((f) => f.id === projectId)
    shareProject.value = {
      id: projectId,
      title: folder?.name ?? p.title ?? p.name ?? projectId,
      isPublic,
    }
    isShareOpen.value = true
  } catch (e) {
    console.error('[App] Failed to load project for sharing', e)
    pushToast('Projekt konnte nicht geteilt werden', 'error')
  }
}

async function onShareConfirm() {
  if (!shareProject.value) return
  if (!shareProject.value.isPublic) {
    try {
      await ProjectService.updateProject(shareProject.value.id, { visibility: 'public' })
    } catch (e) {
      console.error('[App] Failed to make project public', e)
      pushToast('Projekt konnte nicht öffentlich gemacht werden', 'error')
      isShareOpen.value = false
      return
    }
  }
  sharedLinkUrl.value = `${window.location.origin}/?project=${encodeURIComponent(shareProject.value.id)}`
  isShareOpen.value = false
  isSharedLinkOpen.value = true
}

function onSharedLinkCopy() {
  copyTextToClipboard(sharedLinkUrl.value)
  pushToast('Link in die Zwischenablage kopiert')
}

/** Clears the shared-view state and drops the dead ?project= query param. */
function exitSharedView() {
  isSharedView.value = false
  sharedProjectId.value = null
  const url = new URL(window.location.href)
  url.searchParams.delete('project')
  window.history.replaceState({}, '', url.toString())
}

const PENDING_FORK_KEY = 'codelab:pendingFork'

/** Collect the shared project's current (possibly edited) files from fileStore. */
function collectSharedEdits(): { path: string; content: string }[] {
  const proj = sidebarRoot.value?.folders[0]
  if (!proj) return []
  return flattenFiles(proj).map((f) => ({ path: f.path, content: fileStore.get(fileUid(proj.id, f.path)) ?? '' }))
}

/** Fork the source project into the user's namespace and apply the given edits. */
async function materializeFork(
  sourceProjectId: string,
  files: { path: string; content: string }[]
) {
  const fork = await ProjectService.forkProject(sourceProjectId)
  if (files.length) {
    await ProjectService.saveProject(fork.id, 'Bearbeitung nach Fork', files)
  }
  return fork
}

/** If the fork failed because the project is already forked, surface the
 *  overwrite modal instead of an error toast. Returns true when handled. */
function offerOverwrite(err: unknown, files: { path: string; content: string }[]): boolean {
  if (err instanceof ProjectAlreadyForkedError) {
    overwriteTarget.value = { id: err.existingProjectId, title: err.title || 'Projekt' }
    overwriteFiles.value = files
    return true
  }
  return false
}

/** Persist anonymous edits to localStorage so they survive the OAuth redirect. */
function stashPendingFork() {
  if (!sharedProjectId.value) return
  localStorage.setItem(
    PENDING_FORK_KEY,
    JSON.stringify({
      sourceProjectId: sharedProjectId.value,
      files: collectSharedEdits(),
      timestamp: Date.now(),
      attempts: 0,
    })
  )
}

/** After login, fork the stashed source project and apply the saved edits. */
async function consumePendingFork(): Promise<boolean> {
  const raw = localStorage.getItem(PENDING_FORK_KEY)
  if (!raw) return false
  let stash: {
    sourceProjectId?: string
    files?: { path: string; content: string }[]
    timestamp?: number
    attempts?: number
  }
  try {
    stash = JSON.parse(raw)
  } catch {
    localStorage.removeItem(PENDING_FORK_KEY)
    return false
  }
  if (!stash?.sourceProjectId) {
    localStorage.removeItem(PENDING_FORK_KEY)
    return false
  }
  // ponytail: single-key stash; concurrent tabs last-write-wins, acceptable for v1
  // Reject stale stashes (older than 10 min — user likely abandoned the flow).
  if (stash.timestamp && Date.now() - stash.timestamp > 10 * 60 * 1000) {
    localStorage.removeItem(PENDING_FORK_KEY)
    return false
  }
  const attempts = (stash.attempts ?? 0) + 1
  // Persist incremented attempt counter so a future retry can check the bound.
  localStorage.setItem(PENDING_FORK_KEY, JSON.stringify({ ...stash, attempts }))
  try {
    await materializeFork(stash.sourceProjectId, Array.isArray(stash.files) ? stash.files : [])
    // Only clear stash AFTER the fork succeeds.
    localStorage.removeItem(PENDING_FORK_KEY)
    pushToast('Projekt in deinen Namespace geforkt', 'success')
    exitSharedView()
    await loadSidebar()
    return true
  } catch (e) {
    if (offerOverwrite(e, Array.isArray(stash.files) ? stash.files : [])) {
      // Already forked: hand off to the overwrite modal and drop the stash so
      // it won't retry into the same 409.
      localStorage.removeItem(PENDING_FORK_KEY)
      return true
    }
    console.error('[App] Pending fork after login failed', e)
    if (attempts >= 3) {
      // Exhausted retries — abandon the stash and tell the user.
      localStorage.removeItem(PENDING_FORK_KEY)
      pushToast('Änderungen konnten nicht gespeichert werden', 'error')
    }
    return false
  }
}

async function onOverwriteConfirm() {
  const target = overwriteTarget.value
  const files = overwriteFiles.value
  overwriteTarget.value = null
  overwriteFiles.value = []
  if (!target) return
  try {
    if (files.length) {
      await ProjectService.saveProject(target.id, 'Bestehendes Projekt überschrieben', files)
    }
    pushToast('Bestehendes Projekt überschrieben', 'success')
  } catch (e) {
    console.error('[App] Overwrite failed', e)
    pushToast('Überschreiben fehlgeschlagen', 'error')
  }
  exitSharedView()
  startBackendFallbackTimer()
  await loadSidebar()
}

async function onForkConfirm() {
  const mode = forkPrompt.value
  forkPrompt.value = null
  if (!sharedProjectId.value) return

  if (mode === 'anonymous') {
    // Persist edits across the OAuth redirect, then reveal LoginView.
    // ponytail: stash is best-effort single-use; on fork failure edits are lost
    // (could re-stash for retry if needed).
    stashPendingFork()
    // Edits are now persisted to localStorage — mark clean so the browser's
    // beforeunload prompt doesn't fire a false "unsaved changes" warning when
    // LoginView navigates to the OAuth URL.
    isDirty.value = false
    exitSharedView()
    return
  }

  // mode === 'logged-in': fork immediately and carry the current edits.
  const files = collectSharedEdits()
  try {
    await materializeFork(sharedProjectId.value, files)
    pushToast('Projekt in deinen Namespace geforkt', 'success')
  } catch (e) {
    if (offerOverwrite(e, files)) return // modal shown; stay in shared view
    console.error('[App] Failed to fork shared project', e)
    pushToast('Fork fehlgeschlagen', 'error')
    return
  }
  exitSharedView()
  startBackendFallbackTimer()
  await loadSidebar()
}

</script>

<style scoped></style>
